import { V2 } from '../../theme/tokens'
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';
import axios from 'axios';
import KpiRow, { KpiCell } from '../../components/common/KpiRow';
import StatTile from '../../components/common/StatTile';
import TableViewport from '../../components/common/TableViewport';
import KpiCardsSkeleton from '../../Skeletons/KpiCardsSkeleton';
import TableSkeleton from '../../Skeletons/TableSkeleton';
import useFokusNachLaden from '../../components/common/hooks/useFokusNachLaden';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';

export default function CollectionsManagerTab() {
  const [status, setStatus] = useState(null);
  const [metafields, setMetafields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [error, setError] = useState(null);
  // 🔴 K-F1 (2026-10-10): der Knopf sperrt sich waehrend des Laufs und verliert
  // dabei den Fokus — der Haken holt ihn danach zurueck.
  const syncKnopf = useRef(null);
  useFokusNachLaden(syncKnopf, syncing);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [statusRes, metafieldsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/collections/status`),
        axios.get(`${API_BASE_URL}/api/collections/metafields`),
      ]);
      setStatus(statusRes.data);
      setMetafields(metafieldsRes.data.metafields || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load collections data');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async (dryRun = false) => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/collections/sync`,
        { dry_run: dryRun },
        {
          headers: {
            'X-Admin-Key': process.env.REACT_APP_ADMIN_KEY || '',
          },
        }
      );
      setSyncResult(res.data);
      if (!dryRun) {
        // Reload data after sync
        await loadData();
      }
    } catch (err) {
      setSyncResult({
        ok: false,
        error: err.response?.data?.error || 'Sync failed',
        details: err.response?.data?.details,
      });
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    // K-S (UI-Durchgang 2): Seitenaufbau als Geruest der GELADENEN Form, nicht
    // als Kreisel. 🔴 Nachbesserung 2 (Regel-16 Fund 1): hier stand eine
    // Kartenfolge (`CardListSkeleton cards={4}`) — geladen steht aber ein
    // Kennzahlenstreifen ueber ZWEI Tabellen. Die Zahlen sind am geladenen Code
    // gemessen, nicht gewaehlt:
    //   * vier Kacheln — `KpiRow` mit vier `StatTile` (`:180`, `:188`, `:196`, `:226`),
    //   * vier Spalten — „Implemented Collections" (`:257-260`),
    //   * fuenf Spalten — „Available Taxonomy Metafields" (`:314-318`).
    // `framed={false}`: beide geladenen Tabellen stehen selbst in einem
    // `TableViewport` (`:253`, `:310`) — dieselbe Hausform wie
    // `SparePartReviewQueue` und `DiscoveryInbox` (L12 in
    // `Skeletons/ladezustaende.test.jsx`). Die zwei `Card`-Titel der geladenen
    // Flaeche zeichnet das Geruest nicht — es erfindet keinen Text.
    return (
      <Box sx={{ py: { xs: 2, sm: 3 } }}>
        <Box sx={{ mb: 3 }}>
          <KpiCardsSkeleton cards={4} ariaLabel="Loading collection metrics" />
        </Box>
        <Box sx={{ mb: 3 }}>
          <TableSkeleton
            framed={false}
            columns={4}
            rows={4}
            ariaLabel="Loading implemented collections"
          />
        </Box>
        <TableSkeleton
          framed={false}
          columns={5}
          rows={4}
          ariaLabel="Loading taxonomy metafields"
        />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: { xs: 2, sm: 3 } }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ py: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" gutterBottom>
            Collections Manager
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage Shopify dynamic collections based on product taxonomy metafields
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadData}
            disabled={syncing}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={syncing ? <CircularProgress size={16} /> : <SyncIcon />}
            onClick={() => setSyncDialogOpen(true)}
            disabled={syncing}
          >
            Sync to Shopify
          </Button>
        </Box>
      </Box>

      {/* Status Summary */}
      {status && (
        <Box sx={{ mb: 3 }}>
          <KpiRow>
            <KpiCell>
              <StatTile
                label="Total Collections"
                value={status.total_collections}
                hint="Dynamic (smart) collections"
                labelTooltip="Collections automatically maintained via taxonomy rules"
              />
            </KpiCell>
            <KpiCell>
              <StatTile
                label="Attribute-Based"
                value={status.attribute_based_collections}
                hint="Current implementation"
                labelTooltip="Collections using multiple taxonomy attributes"
              />
            </KpiCell>
            <KpiCell>
              <StatTile
                label="Canonical Types"
                value={status.canonical_type_collections}
                hint="Planned expansion"
                labelTooltip="One collection per product type (53 planned)"
                right={
                  /* 🔴 B2 der Regel-15/16-Gates zu PR 4: hier stand
                     `bgcolor: V2.infoText` + `color: V2.infoText` — Schrift
                     und Grund derselbe deckende Wert (`#2563eb`), Kontrast
                     1,00:1, die Beschriftung unsichtbar. Ursache: beim
                     Ersetzen des Literals `#3b82f615` (8 % Alpha) ging der
                     Alphakanal verloren, und derselbe Token wurde fuer Grund
                     UND Schrift gesetzt. `infoSoft` ist der Flaechen-Ton des
                     Paares: `infoText` auf `infoSoft` = 4,75:1 (AA fuer
                     normalen Text). Der Flaechen-Ton ist nie ein `*Text`-Ton
                     — die `*Text`-Werte sind auf Text AUF heller Flaeche
                     abgestimmt, nicht als Fuellung. */
                  <Chip
                    label="Coming Soon"
                    size="small"
                    sx={{
                      bgcolor: V2.infoSoft,
                      color: V2.infoText,
                      fontWeight: 600,
                    }}
                  />
                }
              />
            </KpiCell>
            <KpiCell>
              <StatTile
                label="Metafield Defs"
                value={status.shopify_metafield_defs?.length || 0}
                hint="Available for rules"
                labelTooltip="Taxonomy metafields available for collection rules"
              />
            </KpiCell>
          </KpiRow>
        </Box>
      )}

      {status?.note && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {status.note}
        </Alert>
      )}

      {/* Existing Collections */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Implemented Collections
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            These collections are automatically maintained via the create_dynamic_collections.py script
          </Typography>

          <Box sx={{ mt: 2 }}>
            <TableViewport>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Collection Handle</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {status?.collection_handles?.map((handle) => (
                    <TableRow key={handle} hover>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {handle}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label="Dynamic" size="small" color="primary" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="Active"
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View in Shopify">
                          <IconButton size="small" disabled>
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableViewport>
          </Box>
        </CardContent>
      </Card>

      {/* Available Metafields */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Available Taxonomy Metafields
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            These metafields can be used to create collection rules
          </Typography>

          <Box sx={{ mt: 2 }}>
            <TableViewport>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Metafield Key</TableCell>
                    <TableCell>Namespace</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="center">Used in Collections</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {metafields.map((field) => (
                    <TableRow key={field.key} hover>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace" fontWeight="600">
                          {field.key}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={field.namespace} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {field.type}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{field.description}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        {field.used_in_collections ? (
                          <CheckCircleIcon color="success" fontSize="small" />
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableViewport>
          </Box>
        </CardContent>
      </Card>

      {/* Sync Dialog */}
      <Dialog open={syncDialogOpen} onClose={() => !syncing && setSyncDialogOpen(false)}>
        <DialogTitle>Sync Collections to Shopify</DialogTitle>
        <DialogContent>
          {!syncResult ? (
            <Box>
              <Typography variant="body2" gutterBottom>
                This will run the create_dynamic_collections.py script to sync all dynamic collections to Shopify.
              </Typography>
              <Alert severity="info" sx={{ mt: 2 }}>
                This operation may take 1-2 minutes. Collections will be created or updated based on the current taxonomy rules.
              </Alert>
            </Box>
          ) : (
            <Box>
              {syncResult.ok ? (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {syncResult.message}
                </Alert>
              ) : (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {syncResult.error}
                </Alert>
              )}
              {syncResult.output && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    bgcolor: 'grey.100',
                    borderRadius: 1,
                    maxHeight: 300,
                    overflow: 'auto',
                  }}
                >
                  <Typography variant="caption" component="pre" fontFamily="monospace">
                    {syncResult.output}
                  </Typography>
                </Box>
              )}
              {syncResult.details && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    bgcolor: 'error.lighter',
                    borderRadius: 1,
                    maxHeight: 300,
                    overflow: 'auto',
                  }}
                >
                  <Typography variant="caption" component="pre" fontFamily="monospace">
                    {syncResult.details}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {!syncResult ? (
            <>
              <Button onClick={() => setSyncDialogOpen(false)} disabled={syncing}>
                Cancel
              </Button>
              <Button
                variant="outlined"
                onClick={() => handleSync(true)}
                disabled={syncing}
              >
                Dry run
              </Button>
              {/* 🔴 K-F1 (2026-10-10): der Lauf zeigt sich am Knopf selbst —
                  Ring statt Wortwechsel und statt eines zweiten Spinners im
                  `startIcon`. `loadingPosition="start"` haelt die Beschriftung
                  lesbar (die Vorgabe `center` setzt `color: transparent`). */}
              <Button
                ref={syncKnopf}
                variant="contained"
                onClick={() => handleSync(false)}
                disabled={syncing}
                loading={syncing}
                loadingPosition="start"
                aria-busy={syncing || undefined}
              >
                Sync now
              </Button>
            </>
          ) : (
            <Button
              onClick={() => {
                setSyncDialogOpen(false);
                setSyncResult(null);
              }}
            >
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
